<script setup lang="ts">
import { computed } from 'vue';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import DetailEditorBase from './DetailEditorBase.vue';
import type { TemperatureSchema } from '@apparatix/schemas/Temperature';
import type { ConfidenceIntervalSchema } from '@apparatix/schemas/ConfidenceInterval';
import type { CampaignIntervalSchema } from '@apparatix/schemas/CampaignInterval';
import type { OfficeSchema } from '@apparatix/schemas/Office';
import type { OwnerSchema } from '@apparatix/schemas/Owner';
import type { OpportunityDetailValidationErrors } from '@apparatix/schemas/OpportunityDetail';
import type { OpportunityStatusSchema } from '@apparatix/schemas/OpportunityStatus';
import { InputGroup, Stack, Typography } from '@apparatix/library/core';
import { OpportunityType } from '@apparatix/types/Leads';
import type { Selections } from '@apparatix/composables';

type Props = {
  errors: OpportunityDetailValidationErrors,
  selections: Selections,
  customRightCol?: boolean;
  rightColTitle?: string;
  disableStatusChange: boolean,
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'on:status-change', newStatus): void;
}>();

const anticipatedStart = defineModel<Date>('anticipatedStart');
const budget = defineModel<number>('budget');
const campaignInterval = defineModel<CampaignIntervalSchema>('campaignInterval');
const confidence = defineModel<ConfidenceIntervalSchema>('confidence');
const dueDate = defineModel<Date>('dueDate');
const name = defineModel<string>('name');
const office = defineModel<OfficeSchema>('office');
const owner = defineModel<OwnerSchema>('owner');
const periods = defineModel<number>('periods');
const rate = defineModel<number>('rate');
const source = defineModel<string>('source');
const status = defineModel<OpportunityStatusSchema>('status');
const temperature = defineModel<TemperatureSchema>('temperature');
const type = defineModel<OpportunityType>('type');
const value = defineModel<number>('value');

const rightColHeader = computed(() => {
  if (props.rightColTitle) return props.rightColTitle;
  return type.value === 'Opportunity' ? 'Revenue Projection' : '';
});

const computedStatusOptions = computed(() => {
  if (props.disableStatusChange) {
    return [...props.selections.status, status.value];
  }
  return props.selections.status;
});

const onStatusChange = (event) => {
  emit('on:status-change', event.value);
};
</script>
<template>
  <DetailEditorBase left-col-header="General" :right-col-header="rightColHeader">
    <template #left-col>
      <Stack :gap="5">
        <InputGroup>
          <Typography variant="form-label">
            Name
          </Typography>
          <InputText v-model="name" :invalid="Boolean(props.errors?.name)" fluid />
          <Typography v-if="props.errors?.name" variant="form-error">
            {{ props.errors.name }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Owner
          </Typography>
          <Select
            v-model="owner"
            placeholder="Select Owner"
            option-label="fullName"
            :invalid="Boolean(props.errors.owner)"
            :options="props.selections.owner"
            :option-disabled="(option) => !option.isActive"
            fluid
          />
          <Typography v-if="props.errors?.owner" variant="form-error">
            {{ props.errors.owner }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Status
          </Typography>
          <Select
            v-model="status"
            option-label="status"
            data-key="id"
            :options="computedStatusOptions"
            :disabled="props.disableStatusChange"
            :invalid="Boolean(props.errors?.status)"
            @change="onStatusChange"
          />
          <Typography v-if="props.errors?.status" variant="form-error">
            {{ props.errors.status }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Source
          </Typography>
          <Select
            v-model="source"
            :options="props.selections.sources"
            :disabled="status.state === 'completed'"
            :invalid="Boolean(props.errors?.source)"
          />
          <Typography v-if="props.errors?.source" variant="form-error">
            {{ props.errors.source }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Temperature
          </Typography>
          <Select
            v-model="temperature"
            option-label="temperature"
            :options="props.selections.temperature"
            :invalid="Boolean(props.errors?.temperature)"
          />
          <Typography v-if="props.errors?.temperature" variant="form-error">
            {{ props.errors.temperature }}
          </Typography>
        </InputGroup>
        <InputGroup v-if="type === 'Lead'">
          <Typography variant="form-label">
            Value
          </Typography>
          <InputNumber
            v-model="value"
            input-id="integeronly"
            mode="currency"
            currency="USD"
            :invalid="Boolean(props.errors?.value)"
            fluid
          />
          <Typography v-if="props.errors?.value" variant="form-error">
            {{ props.errors.value }}
          </Typography>
        </InputGroup>
        <template v-if="type === 'Opportunity'">
          <template v-if="props.selections.office.length > 1">
            <InputGroup>
              <Typography variant="form-label">
                Office
              </Typography>
              <Select
                v-model="office"
                option-label="office"
                :invalid="Boolean(props.errors?.office)"
                :options="props.selections.office"
                fluid
              />
              <Typography v-if="props.errors?.office" variant="form-error">
                {{ props.errors.office }}
              </Typography>
            </InputGroup>
          </template>
          <InputGroup>
            <Typography variant="form-label">
              Due Date
            </Typography>
            <DatePicker
              v-model="dueDate"
              :min-date="new Date()"
              :invalid="Boolean(props.errors?.dueDate)"
              fluid
            />
            <Typography v-if="props.errors?.dueDate" variant="form-error">
              {{ props.errors.dueDate }}
            </Typography>
          </InputGroup>
          <InputGroup>
            <Typography variant="form-label">
              Opportunity Budget
            </Typography>
            <InputNumber
              v-model="budget"
              mode="currency"
              currency="USD"
              :min-fraction-digits="0"
              :max-fraction-digits="0"
              :invalid="Boolean(props.errors?.budget)"
              fluid
            />
            <Typography v-if="props.errors?.budget" variant="form-error">
              {{ props.errors.budget }}
            </Typography>
          </InputGroup>
        </template>
      </Stack>
    </template>
    <template v-if="type === 'Opportunity' || props.customRightCol" #right-col>
      <Stack v-if="!props.customRightCol" :gap="5">
        <InputGroup>
          <Typography variant="form-label">
            Confidence
          </Typography>
          <Select
            v-model="confidence"
            option-label="confidencePercentage"
            :options="props.selections.confidence"
            :invalid="Boolean(props.errors?.confidenceInterval)"
          />
          <Typography v-if="props.errors?.confidenceInterval" variant="form-error">
            {{ props.errors.confidenceInterval }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Anticipated Start
          </Typography>
          <DatePicker
            v-model="anticipatedStart"
            :min-date="new Date()"
            :invalid="Boolean(props.errors?.anticipatedStart)"
            fluid
          />
          <Typography v-if="props.errors?.anticipatedStart" variant="form-error">
            {{ props.errors.anticipatedStart }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Rate
          </Typography>
          <InputNumber
            v-model="rate"
            mode="currency"
            currency="USD"
            :invalid="Boolean(props.errors?.rate)"
            fluid
          />
          <Typography v-if="props.errors?.rate" variant="form-error">
            {{ props.errors.rate }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Periods
          </Typography>
          <InputNumber
            v-model="periods"
            mode="decimal"
            :min-fraction-digits="0"
            :max-fraction-digits="2"
            :step="0.25"
            :invalid="Boolean(props.errors?.periods)"
            fluid
          />
          <Typography v-if="props.errors?.periods" variant="form-error">
            {{ props.errors.periods }}
          </Typography>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Interval
          </Typography>
          <Select
            v-model="campaignInterval"
            option-label="selectionValue"
            :options="props.selections.interval"
            :invalid="Boolean(props.errors?.campaignInterval)"
          />
          <Typography v-if="props.errors?.campaignInterval" variant="form-error">
            {{ props.errors.campaignInterval }}
          </Typography>
        </InputGroup>
      </Stack>
      <slot v-if="props.customRightCol" name="right-col" />
    </template>
  </DetailEditorBase>
</template>
